import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useDataFeedWizardStore } from '../../../../store/dataFeedWizard/dataFeedWizardStore';
import { useDanelColumns } from '../../../../pages/DanelPage/hooks/useDanelColumns';
import { DataFeedRowData } from '../../../../types/generatedTypes';
import Actions from '../../../../store/Actions';
import { ViewMode } from '../../../../components/DataFeed/ViewModeToggle/types';
import { ConfigurationTemplate } from '../../../../components/DataFeed/Configuration/types';
import { useIllioUpload } from '../../../../pages/DanelPage/hooks/useIllioUpload';
import { useDisabledRows } from '../../hooks/useDisabledRows';
import { useLinkedAccount } from '../../hooks/useLinkedAccount';
import { useProcessedStatus } from '../../hooks/useProcessedStatus';
import { useTableSearch } from '../../../../hooks/useTableSearch';
import { RowType } from '../../../../types/rowType';
import { coerceValue } from '../../utils/valueCoercion';

export const useReviewAndUploadStep = () => {
  const {
    sessionId,
    fileId,
    templateId,
    setTemplateId,
    setCurrentStepButtonState,
    registerStepActions,
    clearStepActions,
    enabledRowsCount,
    uploadedRowsCount,
    setRowCounts,
    rowsType,
  } = useDataFeedWizardStore();
  const { isRowDisabled } = useDisabledRows();
  const linkedAccountData = useLinkedAccount();
  const apiRef = useGridApiRef();
  const [userColumnWidths, setUserColumnWidths] = useState<Record<string, number>>({});

  const [rows, setRows] = useState<DataFeedRowData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const isPositionsUpload = rowsType === RowType.ROW_TYPE_POSITION;
  const [viewMode, setViewMode] = useState<ViewMode>(isPositionsUpload ? 'processed' : 'raw');
  const [appliedTemplateConfig, setAppliedTemplateConfig] =
    useState<ConfigurationTemplate | null>(null);
  const [autosizeColumns, setAutosizeColumns] = useState(false);
  const [showDisabledAccounts, setShowDisabledAccounts] = useState(false);
  const [unidentifiedRowsCount, setUnidentifiedRowsCount] = useState(0);
  const [rowCountsReady, setRowCountsReady] = useState(false);

  // Use the processed status hook
  const {
    mergedRows,
    processedRows,
    processedFileId,
    reload: reloadProcessedData,
    setProcessedRows,
  } = useProcessedStatus(fileId, rows);

  const { isUploading, uploadToIllio } = useIllioUpload(processedFileId);

  // Cell editing is always allowed - raw mode is protected by processRowUpdate mode check
  const allowCellEditing = true;

  // Determine cell change status for dot indicators
  const getCellClassName = useCallback(
    (params: { field: string; row: DataFeedRowData }) => {
      if (!allowCellEditing) return '';

      const field = params.field;
      const row = params.row;

      // Skip internal fields and special columns
      if (field.startsWith('_') || field === 'id' || field === 'upload_status' || field === 'instrumentType') {
        return '';
      }

      // Check if this cell has been edited (stored in _original_values object)
      const originalValues = (row as Record<string, unknown>)._original_values as Record<string, unknown> | undefined;
      if (!originalValues || !(field in originalValues)) {
        return ''; // Never edited
      }

      const originalValue = originalValues[field];
      const currentValue = row[field as keyof DataFeedRowData];

      const isEmpty = (val: unknown) => val === null || val === undefined || val === '';
      const origEmpty = isEmpty(originalValue);
      const currEmpty = isEmpty(currentValue);

      if (origEmpty && !currEmpty) return 'cell-status-added';    // Green
      if (!origEmpty && currEmpty) return 'cell-status-removed';  // Red
      if (String(originalValue) !== String(currentValue)) {
        return 'cell-status-changed';  // Orange
      }

      return ''; // Value reverted to original
    },
    [allowCellEditing]
  );

  const rowsLeftToUpload = useMemo(() => {
    const enabledRows = rows.filter((row) => !isRowDisabled(row));
    return enabledRows.length - uploadedRowsCount;
  }, [rows, uploadedRowsCount]);

  // Check if any row has edited values (has non-empty _original_values)
  const hasEditedRows = useMemo(() => {
    // Only check processedRows since edits are only on processed data
    return processedRows.some(row => {
      const originalValues = (row as Record<string, unknown>)._original_values as
        Record<string, unknown> | undefined;
      if (!originalValues || Object.keys(originalValues).length === 0) return false;

      // Check if any field has actually changed from original
      return Object.keys(originalValues).some(field => {
        const original = originalValues[field];
        const current = row[field as keyof typeof row];
        return String(current ?? '') !== String(original ?? '');
      });
    });
  }, [processedRows]);

  // Load file data
  const loadData = useCallback(async () => {
    if (!sessionId) {
      console.warn('Missing sessionId - cannot load file data');
      return;
    }

    setIsLoading(true);
    try {
      const response = await Actions.getWizardFileData(sessionId);
      // Add id to each row based on _row_index
      const rowsWithId = (response.rows || []).map(
        (row: DataFeedRowData, index: number) => ({
          ...row,
          id: String(row._row_index ?? index),
          _row_index: row._row_index ?? index,
        })
      );
      setRows(rowsWithId);

      // Calculate initial row counts
      const enabledRows = rowsWithId.filter((row: DataFeedRowData) => !isRowDisabled(row));
      setUnidentifiedRowsCount(enabledRows.filter((row: DataFeedRowData) => !row.instrumentType).length);
    } catch (error) {
      console.error('Failed to load file data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  // Load template config for processed view columns
  const loadTemplateConfig = useCallback(async () => {
    if (!templateId) return;
    try {
      const config = await Actions.getConfigurationTemplate(templateId);
      setAppliedTemplateConfig(config);
    } catch (error) {
      console.error('Failed to load template config:', error);
    }
  }, [templateId]);

  // Load row counts from session
  const loadRowCounts = useCallback(async () => {
    if (!sessionId) return;
    try {
      const session = await Actions.getWizardSession(sessionId);
      setRowCounts(session.enabled_rows_count ?? 0, session.uploaded_rows_count ?? 0);
    } catch (error) {
      console.error('Failed to load row counts:', error);
    }
  }, [sessionId, setRowCounts]);

  // Load all data on mount
  useEffect(() => {
    if (!sessionId) return;

    setRowCountsReady(false);
    Promise.all([loadData(), loadTemplateConfig(), loadRowCounts()]).then(() => {
      setRowCountsReady(true);
    });
  }, [sessionId, templateId, loadData, loadTemplateConfig, loadRowCounts]);

  // Auto-trigger processing for positions (no template needed)
  useEffect(() => {
    if (!isPositionsUpload || !sessionId) return;

    const autoProcess = async () => {
      setIsLoading(true);
      try {
        await Actions.applyTemplateRules(undefined, sessionId);
        await reloadProcessedData();
        await loadData();
      } finally {
        setIsLoading(false);
      }
    };
    autoProcess();
  }, [isPositionsUpload, sessionId]);

  // Clear selection and search when view mode changes
  useEffect(() => {
    setSelectedRowIds([]);
    handleSearch('');
  }, [viewMode]);

  // Process row update - calls API to update cell (only for processed mode)
  const processRowUpdate = useCallback(
    async (
      newRow: DataFeedRowData,
      oldRow: DataFeedRowData
    ): Promise<DataFeedRowData> => {
      // Cell editing only allowed in processed mode
      if (viewMode !== 'processed') {
        console.warn('Cell editing only allowed in processed mode');
        return oldRow;
      }

      if (!sessionId) {
        console.warn('Missing sessionId - cannot update cell');
        return newRow;
      }

      if (!fileId) {
        console.warn('Missing fileId - cannot update cell');
        return oldRow;
      }

      // Find what changed
      const changedFields = Object.keys(newRow).filter(
        key =>
          key !== 'id' &&
          newRow[key as keyof DataFeedRowData] !==
          oldRow[key as keyof DataFeedRowData]
      );

      if (changedFields.length === 0) {
        return newRow;
      }

      // Optimistic update
      setProcessedRows(prev => prev.map(row => (row.id === newRow.id ? newRow : row)));

      // Update each changed field and sync with backend response
      let finalRow = newRow;
      for (const field of changedFields) {
        const rawValue = newRow[field as keyof DataFeedRowData];
        const newValue = coerceValue(rawValue);

        try {
          // Use _processed_index for processed rows
          const processedIndex = (newRow as DataFeedRowData & { _processed_index?: number })._processed_index;
          if (processedIndex === undefined) {
            console.error('Missing _processed_index for processed row update');
            return oldRow;
          }

          const response = await Actions.updateProcessedCell(
            sessionId,
            processedIndex,
            field,
            newValue as string | number | boolean | null,
            fileId
          );

          // Sync frontend state with backend response
          if (response.updated_row) {
            finalRow = {
              ...finalRow,
              ...response.updated_row,
              id: newRow.id,
            } as DataFeedRowData;
          }
        } catch (error) {
          console.error(
            `Failed to update cell [${field}]:`,
            error
          );
          // Revert UI on error
          setProcessedRows(prev =>
            prev.map(row => (row.id === newRow.id ? oldRow : row))
          );
          return oldRow;
        }
      }

      // Update rows with final state from backend
      setProcessedRows(prev => prev.map(row => (row.id === newRow.id ? finalRow : row)));
      return finalRow;
    },
    [sessionId, fileId, viewMode, setProcessedRows]
  );

  // Get display rows based on view mode and filter by disabled accounts
  const displayRows = useMemo(() => {
    const baseRows = viewMode === 'processed' ? processedRows : mergedRows;
    if (showDisabledAccounts) {
      return baseRows;
    }
    return baseRows.filter((row) => !isRowDisabled(row));
  }, [viewMode, processedRows, mergedRows, showDisabledAccounts]);

  // Enrich raw-mode rows with computed linked account value so useTableSearch can filter on it
  const searchableRows = useMemo(() => {
    if (
      viewMode !== 'raw' ||
      !linkedAccountData?.hasLinkedAccounts ||
      !linkedAccountData.accountNumberColumn
    ) {
      return displayRows;
    }
    const { linkedAccountMap, accountNumberColumn } = linkedAccountData;
    return displayRows.map(row => ({
      ...row,
      __linked_account__: linkedAccountMap.get(String(row[accountNumberColumn])) ?? '',
    }));
  }, [displayRows, viewMode, linkedAccountData]);

  // Search filtering on display rows
  const {
    searchTerm,
    handleSearch,
    filteredData: searchFilteredRows,
  } = useTableSearch<DataFeedRowData>(searchableRows, { debounceMs: 300 });

  const { columns } = useDanelColumns({
    rows: displayRows,
    processRowUpdate,
    autosizeColumns: false,
    viewMode,
    appliedTemplateConfig,
    allowCellEditing,
    rowsType,
    linkedAccountData,
  });

  const columnsWithUserWidths = useMemo(() => {
    if (Object.keys(userColumnWidths).length === 0) return columns;
    return columns.map((col: any) => {
      const userWidth = userColumnWidths[col.field];
      if (userWidth !== undefined) {
        const { flex: _flex, ...rest } = col;
        return { ...rest, width: userWidth };
      }
      return col;
    });
  }, [columns, userColumnWidths]);

  // Handle autosize state changes - must be after columns definition
  useEffect(() => {
    const preservedColumns = [
      '__check__',
      '__tree_data_group__',
      'uploadStatus',
    ];

    if (autosizeColumns && apiRef.current) {
      const allColumns = apiRef.current.getAllColumns();
      const visibleUserColumns = allColumns
        .filter(col => !preservedColumns.includes(col.field))
        .map(col => col.field);

      if (visibleUserColumns.length > 0) {
        apiRef.current.autosizeColumns({
          includeHeaders: true,
          includeOutliers: true,
          expand: true,
          columns: visibleUserColumns,
        });
      }
    }
  }, [autosizeColumns, apiRef, viewMode, columns]);

  const handleColumnWidthChange = useCallback(
    (params: { colDef: { field: string }; width: number }) => {
      setUserColumnWidths(prev => ({ ...prev, [params.colDef.field]: params.width }));
    },
    []
  );

  // Row selection model for DataGrid Premium
  const rowSelectionModel = {
    type: 'include' as const,
    ids: new Set(selectedRowIds),
  };

  // Handle row selection change
  const handleRowSelectionModelChange = useCallback(
    (newSelection: unknown) => {
      try {
        let newSelectionArray: string[] = [];

        // Get all available row IDs for select all functionality
        const allRowIds = displayRows.map(row => row.id);

        // Handle different selection formats from DataGrid Premium
        if (
          newSelection &&
          typeof newSelection === 'object' &&
          'ids' in newSelection
        ) {
          const selection = newSelection as { type: string; ids: Set<string> };
          if (selection.type === 'exclude') {
            // "Select all except" - if empty ids, it means select all
            if (selection.ids.size === 0) {
              newSelectionArray = allRowIds;
            } else {
              // Select all except the excluded ones
              const excludedIds = Array.from(selection.ids);
              newSelectionArray = allRowIds.filter(
                id => !excludedIds.includes(id)
              );
            }
          } else {
            // Regular include type or default - extract the Set from the ids property
            newSelectionArray = Array.from(selection.ids);
          }
        } else if (Array.isArray(newSelection)) {
          newSelectionArray = newSelection as string[];
        } else if (newSelection instanceof Set) {
          newSelectionArray = Array.from(newSelection);
        }

        // Validate that all selected IDs exist in displayRows and filter out disabled rows
        const validRowIds = new Set(displayRows.map(row => row.id));
        const validatedSelection = newSelectionArray
          .filter(id => validRowIds.has(id))
          .filter(id => {
            const row = displayRows.find(r => r.id === id);
            return row ? !isRowDisabled(row) : false;
          });

        setSelectedRowIds(validatedSelection);
      } catch {
        // Clear selection on error to prevent UI issues
        setSelectedRowIds([]);
      }
    },
    [displayRows]
  );

  // Handle upload to Illio
  const handleUploadToIllio = useCallback(async () => {
    if (selectedRowIds.length === 0) return;

    // Map selected row IDs to processed indices for upload API
    const rowIndicesToUpload = displayRows
      .filter(row => selectedRowIds.includes(row.id))
      .map(
        row =>
          (row as DataFeedRowData & { _processed_index?: number })
            ._processed_index as number
      )
      .filter(index => index !== undefined);

    if (rowIndicesToUpload.length === 0) {
      console.warn('No valid row indices found for upload');
      return;
    }

    const result = await uploadToIllio(rowIndicesToUpload);

    // Update row counts in store from API response
    // This will automatically trigger refetch in all useProcessedStatus instances
    // (e.g., ShowFeedModal) since they depend on uploadedRowsCount
    if (result) {
      const enabledCount = result.enabled_rows_count ?? 0;
      const uploadedCount = result.uploaded_rows_count ?? 0;
      setRowCounts(enabledCount, uploadedCount);
    }

    await Promise.all([reloadProcessedData(), loadData()]);
    setSelectedRowIds([]);
  }, [
    selectedRowIds,
    displayRows,
    uploadToIllio,
    reloadProcessedData,
    loadData,
    setRowCounts,
  ]);

  // Check if all enabled rows have been uploaded (using counts from store)
  const allRowsUploaded =
    enabledRowsCount > 0 && uploadedRowsCount === enabledRowsCount;

  // Update button state
  useEffect(() => {
    setCurrentStepButtonState({
      disabled: isUploading,
      loading: isUploading,
      validationMessage: undefined,
    });
  }, [isUploading, setCurrentStepButtonState]);


  // Register step actions
  useEffect(() => {
    registerStepActions({});
    return () => clearStepActions();
  }, [registerStepActions, clearStepActions]);

  // Handle apply configuration success - reload data with updated instrument types
  const handleApplySuccess = useCallback(async () => {
    setIsLoading(true);
    try {
      await loadData();
      await reloadProcessedData();
      await loadTemplateConfig();
      setViewMode('processed');
    } finally {
      setIsLoading(false);
    }
  }, [loadData, reloadProcessedData, loadTemplateConfig]);

  return {
    rows: searchFilteredRows,
    columns: columnsWithUserWidths,
    isLoading,
    processRowUpdate,
    isUploading,
    selectedRowIds,
    rowSelectionModel,
    handleRowSelectionModelChange,
    handleUploadToIllio,
    viewMode,
    setViewMode,
    hasProcessedData: processedRows.length > 0,
    handleApplySuccess,
    sessionId,
    templateId,
    setTemplateId,
    isRowDisabled,
    autosizeColumns,
    setAutosizeColumns,
    showDisabledAccounts,
    setShowDisabledAccounts,
    apiRef,
    unidentifiedRowsCount,
    rowsLeftToUpload,
    rowCountsReady,
    getCellClassName,
    hasEditedRows,
    searchTerm,
    handleSearch,
    isPositionsUpload,
    handleColumnWidthChange,
  };
};
